import template from './ps-select.html';
import './ps-select.css';

/**
 * @class PSSelectViewModel
 * @param {Object} params - The parameters for the component.
 * @param {Object} rootElement - The root element of the component.
 *
 * @param {Boolean} [params.disabled=false] - Whether the component is disabled. Observable: `this.disabled`.
 * @param {Boolean} [params.hideSelected=false] - Whether to hide selected options in display text. Observable: `this.hideSelected`.
 * @param {String} [params.placeholder='Please make a selection'] - Placeholder text when no option is selected. Observable: `this.placeholder`.
 * @param {String} [params.inputClasses='ps-input'] - CSS classes for the input element. Observable: `this.inputClasses`.
 * @param {Boolean} [params.enableMultiple=false] - Enable multiple selection. When `false`, uses `params.value`; when `true`, uses `params.values`. Observable: `this.enableMultiple`.
 * @param {Boolean} [params.enableSearch=false] - Enable search in dropdown. Observable: `this.enableSearch`.
 * @param {Function|null} [params.searchFunction=null] - Custom search function: `(searchText, component) => Array`. Overrides default search.
 * @param {Boolean} [params.enableSorting=true] - Enable sorting of options. Observable: `this.enableSorting`.
 * @param {String} [params.sortField=null] - Field name to sort by. Falls back to `optionsText` or `optionsValue` if null. Observable: `this.sortField`.
 * @param {Function|null} [params.sortFunction=null] - Custom sort function: `(options, sortField) => void` (sorts in-place). Overrides default sorting. Not observable.
 * @param {Array} [params.options=[]] - Options to display. Can be objects or primitives. Observable: `this.options`.
 * @param {String} [params.optionsText=null] - Property name for option display text when options are objects. Uses `toString()` if null. Observable: `this.optionsText`.
 * @param {String} [params.optionsValue=null] - Property name for option values when options are objects. Uses entire object if null. Observable: `this.optionsValue`.
 * @param {Array} [params.values=[]] - Selected values (multiple selection mode). Observable: `this.values`.
 * @param {Function|null} [params.optionsFormatter=null] - Custom formatter: `(option, component) => String` (HTML). Uses `optionsText` or `toString()` if null.
 * @param {Boolean} [params.can_type_new=false] - When true, the input becomes editable so the user can type a custom value not in the options list. Only works when enableMultiple is false.
 *
 * @returns {void}
 */

class PSSelectViewModel {
	constructor(params, rootElement) {
		this.params = params;
		this.rootElement = rootElement;

		this.disabled = ko_helper.safe_observable(params.disabled, false);
		this.hideSelected = ko_helper.safe_observable(params.hideSelected, false);
		this.placeholder = ko_helper.safe_observable(params.placeholder, 'Please make a selection');
		this.inputClasses = ko_helper.safe_observable(params.inputClasses, 'ps-input');
		this.enableMultiple = ko_helper.safe_observable(params.enableMultiple, false);
		this.enableSearch = ko_helper.safe_observable(params.enableSearch, false);
		this.searchFunction = params.searchFunction ?? null;
		this.enableSorting = ko_helper.safe_observable(params.enableSorting, true);
		this.sortField = ko_helper.safe_observable(params.sortField, null);
		this.sortFunction = params.sortFunction ?? null;

		this.options = ko_helper.safe_observableArray(params.options, []);
		this.optionsText = ko_helper.safe_observable(params.optionsText, null);
		this.optionsValue = ko_helper.safe_observable(params.optionsValue, null);
		this.values = ko_helper.safe_observableArray(params.values, []);
		this.optionsFormatter = params.optionsFormatter ?? null;
		this.can_type_new = ko_helper.safe_observable(params.can_type_new, false);
		this.typedText = ko.observable('');

		// If multiple is not enabled, we need to manage the value and values observables separately.
		if (!this.enableMultiple()) {
			this.value = ko_helper.safe_observable(params.value, null);

			this.value.subscribe((newValue) => {
				let oldArrayValue = this.values()[0];

				if (oldArrayValue != newValue) {
					if (newValue) this.values([newValue]);
					else this.values([]);
				}
			});

			this.values.subscribe((newList) => {
				if (newList.length == 0) {
					this.value(null);
					return;
				}

				let newArrayValue = newList[0];
				let oldValue = this.value();

				if (newArrayValue && oldValue != newArrayValue)
					this.value(newArrayValue);
			});

			if (this.values().length == 0 && this.value())
				this.values([this.value()]);
		}

		this.externalOptions = ko.observableArray();
		this.isOpen = ko.observable(false);
		this.searchText = ko.observable('');

		// This is used to determine if the dropdown can be opened. Setting disabled, or if the options are empty will prevent the dropdown from being opened.
		// When can_type_new is enabled, the dropdown can always open (so the user can type).
		this.canOpen = ko.pureComputed(() => {
			if (this.disabled()) return false;
			if (this.can_type_new()) return true;
			return this.options().length > 0;
		});

		// This is the list of selected options based on the values property and the activeOptions list. If an option is not found in the activeOptions list,
		// it will be added to the list with a temporary placeholder value.
		this.selectedOptions = ko.pureComputed(() => {
			let selected = [];
			let options = this.activeOptions() ?? [];
			let values = this.values() ?? [];

			values.forEach(value => {
				// Skip null, undefined, or empty values
				if (value === null || value === undefined || value === '') return;

				let option = options.find(option => {
					if (this.optionsValue()) return option[this.optionsValue()] == value;
					else return option == value;
				});

				if (option) selected.push(option);
				else {
					let placeholderOption = {};

					if (typeof value != 'object') {
						if (this.optionsText()) placeholderOption[this.optionsText()] = `Selected: ${value}`;
						if (this.optionsValue()) placeholderOption[this.optionsValue()] = value;
					}
					else // If value is already an object, use it as is
						placeholderOption = value;

					selected.push(placeholderOption);
				}
			});

			return selected;
		});

		// This is the text that is displayed in the input field. It includes the selected options, and the placeholder.
		// hideSelected is used to hide the selected options and force display of the placeholder.
		this.displayText = ko.pureComputed(() => {
			if (!this.hideSelected() && this.selectedOptions().length > 0) {
				if (this.optionsText()) {
					let text = this.selectedOptions()
						.filter(opt => opt != null)
						.map(opt => {
							const o = typeof opt === 'function' ? opt() : opt;
							const val = o[this.optionsText()];
							return typeof val === 'function' ? val() : (val || 'Unknown');
						})
						.join(', ');

					return text;
				}
				else {
					return this.selectedOptions()
						.filter(opt => opt != null) // Filter out null/undefined options
						.join(', ');
				}
			}
			else return this.placeholder() ?? 'Please make a selection';
		});

		// Placeholder shows when no selections (or hidden) and displayText equals placeholder
		// Used for css styling
		this.isPlaceholder = ko.pureComputed(() => {
			if (!this.hideSelected() && this.selectedOptions().length > 0) return false;
			let text = this.displayText();
			let placeholder = this.placeholder() ?? 'Please make a selection';
			return text === placeholder;
		});

		// This is the list of active options that the component knows about, including selected and external options.
		this.activeOptions = ko.pureComputed(() => {
			let selected = this.selectedOptions() ?? [];
			let externalOptions = this.externalOptions() ?? [];
			let options = this.options() ?? [];

			if (externalOptions.length > 0)
				options = externalOptions;

			let merged = options.concat(selected);
			return merged;
		});

		// This is the list of options that are displayed in the dropdown. It includes only the original options, and external options.
		this.visibleOptions = ko.pureComputed(() => {
			let externalOptions = this.externalOptions() ?? [];
			let options = this.options() ?? [];

			if (externalOptions.length > 0)
				options = externalOptions;

			this.sortOptions(options);
			return options;
		});

		// When can_type_new is enabled, sync typedText with the current value
		if (this.can_type_new() && !this.enableMultiple()) {
			// Initialize typedText from current value
			let initVal = this.value ? this.value() : null;
			if (initVal !== null && initVal !== undefined)
				this.typedText(this.getOptionTextFromValue(initVal));

			// When value changes externally (e.g. from dropdown selection), update typedText
			if (this.value) {
				this.value.subscribe((newValue) => {
					const nextText = this.getOptionTextFromValue(newValue);
					if (nextText !== this.typedText())
						this.typedText(nextText);
				});
			}
		}

		// These add event listeners to the global document object to handle the dropdown positioning on scroll/resize/click and auto-closing when clicking outside of it.
		document.addEventListener('click', this.globalClickHandler.bind(this));
		document.addEventListener('scroll', this.positionDropdown.bind(this));
		document.addEventListener('resize', this.positionDropdown.bind(this));

		// Handle async fetches of options for initial results
		if (this.options().length === 0 && this.searchFunction && this.searchFunction instanceof Function)
			this.search();
	}

	/**
	 * Confirm typed text as the selected value. Used when can_type_new is enabled.
	 */
	confirmTypedText() {
		if (!this.can_type_new() || this.enableMultiple()) return;

		let text = (this.typedText() || '').trim();
		if (!text) {
			this.value(null);
			return;
		}

		// If the user typed (or blur committed) the label of an existing option,
		// map it back to the real `optionsValue` (typically an id / lookup id).
		if (this.optionsText() && this.optionsValue()) {
			const typedLower = text.toLowerCase();
			// Avoid activeOptions() here to prevent circular computed evaluation.
			const options = (this.externalOptions()?.length > 0 ? this.externalOptions() : this.options()) ?? [];

			const match = options.find(opt => {
				if (!opt) return false;
				let label = opt[this.optionsText()];
				if (typeof label === 'function') label = label();
				label = (label ?? '').toString();
				return label.toLowerCase() === typedLower;
			});

			if (match)
				this.value(match[this.optionsValue()]);
			else
				this.value(text);
		}
		else
			this.value(text);
	}

	/**
	 * Check if an option is selected.
	 * @param {Object} option - The option to check if it is selected.
	 * @returns {Boolean} True if the option is selected, false otherwise.
	 */
	isSelected(option) {
		if (!option) return false;

		let match = this.values().find(value => {
			if (this.optionsValue()) return value == option[this.optionsValue()];
			else return value == option;
		});

		if (match) return true;
		else return false;
	}

	/**
	 * Get the HTML to render for an option.
	 * @param {Object} option - The option to get the HTML for.
	 * @returns {String} The HTML for the option.
	 */
	getOptionHtml(option) {
		if (!option) return '';
		else if (this.optionsFormatter && this.optionsFormatter instanceof Function)
			return this.optionsFormatter(option, this);
		else if (this.optionsText())
			return option[this.optionsText()];
		else
			return option.toString();
	}

	/**
	 * Get the value of an option.
	 * @param {Object} option - The option to get the value for.
	 * @returns {Object} The value of the option.
	 */
	getOptionValue(option) {
		if (this.optionsValue()) return option[this.optionsValue()];
		else return option;
	}

	/**
	 * Resolve the display text for a given selected value.
	 * Used by `can_type_new` to keep the typed input in sync with the selected
	 * lookup id (optionsValue) -> label (optionsText).
	 */
	getOptionTextFromValue(value) {
		if (value === null || value === undefined) return '';

		// Avoid activeOptions() here to prevent circular computed evaluation.
		let options = (this.externalOptions()?.length > 0 ? this.externalOptions() : this.options()) ?? [];
		let match = options.find(option => {
			if (this.optionsValue()) return option[this.optionsValue()] == value;
			else return option == value;
		});

		if (match && this.optionsText()) {
			let text = match[this.optionsText()];
			return (typeof text === 'function') ? (text() ?? '') : (text ?? '');
		}

		return String(value);
	}

	/**
	 * Toggle the open state of the dropdown.
	 * @param {Boolean} expected - The expected state of the dropdown. If not provided, it will be toggled to the opposite of the current state.
	 */
	toggleOpen(expected) {
		if (expected === undefined)
			expected = !this.isOpen();

		if (!this.canOpen())
			expected = false;

		this.isOpen(expected);
		if (expected)
			this.positionDropdown();
		else {
			// Confirm any typed text when closing the dropdown
			if (this.can_type_new())
				this.confirmTypedText();

			this.resetSearch();
		}
	}

	/**
	 * Toggle the selected state of an option.
	 * @param {Object} option - The option to toggle the selected state of. If allowMultiple is false, the option will be set as the only selected option if selected.
	 */
	toggleSelected(option) {
		let optionValue = this.getOptionValue(option);
		let values = this.values() ?? [];

		if (this.enableMultiple()) {
			if (values.includes(optionValue))
				values.splice(values.indexOf(optionValue), 1);
			else
				values.push(optionValue);
		}
		else {
			if (values[0] == optionValue)
				values = [];
			else {
				values = [optionValue];
			}

			// When can_type_new is enabled, update typedText to reflect the selected value and close dropdown
			if (this.can_type_new()) {
				let nextText = '';
				if (values.length > 0) {
					if (this.optionsText()) {
						let label = option[this.optionsText()];
						if (typeof label === 'function') label = label();
						nextText = (label ?? '').toString();
					}
					else
						nextText = option?.toString?.() ?? String(optionValue);
				}

				// typedText is what the input displays; confirmTypedText will map
				// label back to optionsValue when closing.
				this.typedText(nextText);
				// Update values before closing, so confirmTypedText can resolve
				// label -> optionsValue using activeOptions().
				this.values(values);
				this.toggleOpen(false);
				return;
			}
			else {
				this.toggleOpen(false);
			}
		}

		this.values(values);
	}

	/**
	 * BUG: Knockout does not seem to automatically call the dispose method when the component is removed from the DOM. This is a placeholder for when that is fixed.
	 * Dispose of the component and remove the event listeners.
	 */
	dispose() {
		document.removeEventListener('click', this.globalClickHandler);
		document.removeEventListener('scroll', this.positionDropdown);
		document.removeEventListener('resize', this.positionDropdown);
	}

	/**
	 * Global click handler to close the dropdown when clicking outside of it.
	 * @param {Event} e - The event object.
	 */
	globalClickHandler = (e) => {
		if (this.isOpen() && !this.rootElement.contains(e.target))
			this.toggleOpen(false);

		// Return true to allow the event to bubble up to the document
		return true;
	};

	/**
	 * Position the dropdown relative to the root element.
	 * @returns {void}
	 */
	positionDropdown() {
		if (!this.isOpen()) return;

		let inputElement = this.rootElement.querySelector('div.ps-element-group');
		let dropdownElement = this.rootElement.querySelector('.ps-dropdown');

		let inputPosition = inputElement.getBoundingClientRect();
		let dropdownPosition = dropdownElement.getBoundingClientRect();
		let top = 0;
		let left = 0;

		let dropdownHeight = dropdownPosition.height;
		let windowHeight = window.innerHeight;

		dropdownElement.style.minWidth = `${inputPosition.width}px`;

		// Dropdown left check
		if ((inputPosition.left + dropdownPosition.width) > window.innerWidth)
			left = Math.min(inputPosition.right, window.innerWidth - 10) - dropdownPosition.width;
		else
			left = inputPosition.left;

		// Dropup check
		if (inputPosition.top + inputPosition.height + dropdownHeight > windowHeight)
			top = inputPosition.top - dropdownHeight - 2;
		else
			top = inputPosition.top + inputPosition.height + 2;

		let check = null;
		let currentEl = this.rootElement;
		while (currentEl) {
			let style = getComputedStyle(currentEl, null);

			// Elements with transform, perspective or filter will be considered the bounding box for all child elements with position: fixed
			// To get back to the original position, we need to subtract the position of the element from the bounding box
			check = style.getPropertyValue('transform');
			if (check !== 'none')
				break;

			check = style.getPropertyValue('perspective');
			if (check !== 'none')
				break;

			check = style.getPropertyValue('filter');
			if (check !== 'none')
				break;

			currentEl = currentEl.parentElement;
		}
		if (currentEl) {
			let modal_position = currentEl.getBoundingClientRect();
			left -= modal_position.left;
			top -= modal_position.top;
		}

		dropdownElement.style.left = `${left}px`;
		dropdownElement.style.top = `${top}px`;

		if (this.enableSearch())
			this.rootElement.querySelector('input[name="searchText"]').focus();
	}

	/**
	 * Reset the search text and initiate a search to reset the visible options.
	 */
	resetSearch() {
		this.searchText('');
		this.search();
	}

	/**
	 * Search for options based on the search text. If no searchFunction is provided, the options will be filtered based on the optionsText and optionsValue properties.
	 * @returns {void}
	 */
	search() {
		let searchText = this.searchText();
		let matches = [];

		if (this.searchFunction && this.searchFunction instanceof Function) {
			matches = this.searchFunction(searchText, this);
			this.externalOptions(matches);
		}
		else {
			let options = this.options();
			matches = options.filter(option => {
				if (this.optionsText())
					if ((option[this.optionsText()] ?? '').toString().toLowerCase().includes(searchText.toLowerCase())) return true;

					else
						if ((option ?? '').toString().toLowerCase().includes(searchText.toLowerCase())) return true;

				if (this.optionsValue())
					if ((option[this.optionsValue()] ?? '').toString().toLowerCase().includes(searchText.toLowerCase())) return true;
					else
						return false;
			});
		}

		this.externalOptions(matches);
	}

	/**
	 * Sort the options in-place based on the sortField and sortFunction properties.
	 * @param {Array} options - The options to sort.
	 * @returns {void}
	 */
	sortOptions(options) {
		if (!this.enableSorting()) return; // Safety check to prevent sorting if it is disabled.

		let sortField = this.sortField() ?? this.optionsText() ?? this.optionsValue();

		if (this.sortFunction && this.sortFunction instanceof Function)
			options = this.sortFunction(options, sortField);
		else {
			options.sort((a, b) => {
				let aText = a[sortField];
				let bText = b[sortField];

				if (typeof aText === 'string' && typeof bText === 'string')
					return aText.toLowerCase().localeCompare(bText.toLowerCase());
				else return aText < bText ? -1 : 1;
			});
		}
	}
}

export default {
	name: 'ps-select',
	viewModel: PSSelectViewModel,
	provider: 'ko',
	template: template
};
